import { useAtomValue, useSetAtom } from 'jotai'
import { ReactNode } from 'react'

import { SettingsDropdownSection, SettingsBox } from '@cowprotocol/ui'

import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'

import { useIsInfiniteApproveDisabledInWidget } from 'modules/injectedWidget'
import {
  limitOrdersSettingsAtom,
  updateLimitOrdersSettingsAtom,
} from 'modules/limitOrders/state/limitOrdersSettingsAtom'
import { PriceChartSettings } from 'modules/priceChart'
import { getOrdersTableSettings } from 'modules/trade/const/common'
import { SettingsContainer } from 'modules/trade/pure/Settings'

import { AdvancedOrdersSettingsState } from '../../state/advancedOrdersSettingsAtom'

export interface SettingsProps {
  state: AdvancedOrdersSettingsState
  onStateChanged: (state: Partial<AdvancedOrdersSettingsState>) => void
}

export function AdvancedOrdersSettingsDropdown({ state, onStateChanged }: SettingsProps): ReactNode {
  const { showRecipient, enablePartialApprovalBySettings } = state
  // TODO: we should use limit orders settings in Advanced Orders!
  const limitOrdersSettings = useAtomValue(limitOrdersSettingsAtom)
  const updateLimitOrdersSettings = useSetAtom(updateLimitOrdersSettingsAtom)
  const { LEFT_ALIGNED } = getOrdersTableSettings()
  const { disableCustomRecipient } = useInjectedWidgetParams()
  const isPartialApprovalLockedByWidget = useIsInfiniteApproveDisabledInWidget()
  const { i18n } = useLingui()

  return (
    <SettingsContainer>
      <SettingsDropdownSection title={t`TWAP Settings`}>
        {!disableCustomRecipient && (
          <SettingsBox
            title={t`Custom Recipient`}
            tooltip={t`Allows you to choose a destination address for the swap other than the connected one.`}
            checked={showRecipient}
            toggle={() => onStateChanged({ showRecipient: !showRecipient })}
          />
        )}

        <SettingsBox
          title={t`Enable Partial Approvals`}
          tooltip={t`Allows you to set partial token approvals instead of full approvals.`}
          checked={isPartialApprovalLockedByWidget ? true : enablePartialApprovalBySettings}
          toggle={() => onStateChanged({ enablePartialApprovalBySettings: !enablePartialApprovalBySettings })}
          disabled={isPartialApprovalLockedByWidget}
        />
      </SettingsDropdownSection>

      <SettingsDropdownSection title={t`TWAP Interface`}>
        <SettingsBox
          title={i18n._(LEFT_ALIGNED.title)}
          tooltip={i18n._(LEFT_ALIGNED.tooltip)}
          checked={limitOrdersSettings.ordersTableOnLeft}
          toggle={() => updateLimitOrdersSettings({ ordersTableOnLeft: !limitOrdersSettings.ordersTableOnLeft })}
        />
        <PriceChartSettings />
      </SettingsDropdownSection>
    </SettingsContainer>
  )
}
